import IdvIntro from '@/components/idv/IdvIntro';
import { useTrackPageViewed } from '@/services/analytics/useTracking';
import { OnboardingData } from '@common/types';
import { typedKeys } from '@common/util/objects';
import { getIsValidZipCode } from '@common/util/strings';
import { useState } from 'react';
import { IdvForm } from '../IdvForm';

type T = Required<OnboardingData>['employerAddress'];

export const EmployerAddressView = () => {
    useTrackPageViewed('IDV > EmployerAddressView');

    const [values, setValues] = useState<T>();

    const updateValues = (change: T) => {
        setValues({ ...values, ...change });
    };

    return (
        <>
            <IdvIntro
                title="What is your employer’s address?"
                desc="We use this to verify your identity. If you work for a company with multiple offices, the US headquarter address works best."
            />
            <IdvForm<T>
                initValues={(userProfile) => {
                    const employerAddress =
                        userProfile?.onboardingData?.employerAddress ||
                        undefined;
                    return {
                        address1: employerAddress?.address1 || undefined,
                        locality: employerAddress?.locality || undefined,
                        region: employerAddress?.region || undefined,
                        postalCode: employerAddress?.postalCode || undefined,
                        address2: employerAddress?.address2 || undefined,
                    };
                }}
                toMutationInput={(values) => ({
                    onboardingData: {
                        employerAddress: values,
                    },
                })}
                backNextNavProps={{
                    onClickEventParams: {
                        location: 'IDV > EmployerAddressView',
                    },
                }}
            >
                <IdvForm.Row>
                    <IdvForm.Input<T>
                        name="address1"
                        label="Street"
                        placeholder="Start typing to autocomplete your employer's address"
                        type="address"
                        onChange={(address: T, updateValues) => {
                            updateValues?.(
                                typedKeys(address).reduce((acc, key) => {
                                    const value = values?.[key];
                                    if (!value || key === 'address1')
                                        return { ...acc, [key]: address[key] };
                                    return acc;
                                }, values || {})
                            );
                        }}
                        isRequired
                        autoFocus
                    />
                </IdvForm.Row>
                <IdvForm.Row>
                    <IdvForm.Input<T>
                        name="locality"
                        label="City"
                        placeholder="eg. San Francisco"
                        onChange={(locality) => updateValues({ locality })}
                    />
                    <IdvForm.Input<T>
                        name="region"
                        label="State"
                        type="usaState"
                        placeholder="eg. California"
                        onChange={(region) => updateValues({ region })}
                    />
                </IdvForm.Row>
                <IdvForm.Row align="flex-start">
                    <IdvForm.Input<T>
                        name="postalCode"
                        label="Zip code"
                        placeholder="eg. 94941"
                        onChange={(postalCode) => updateValues({ postalCode })}
                        validateOnBlur
                        validations={[
                            {
                                validate: getIsValidZipCode,
                                error: 'Please enter a valid zip code',
                            },
                        ]}
                    />
                    <IdvForm.Input<T>
                        name="address2"
                        label="Apt number"
                        placeholder="If applicable"
                    />
                </IdvForm.Row>
            </IdvForm>
        </>
    );
};
